import { iconButtonClasses, IconButton as MuiIconButton, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import React, { forwardRef } from 'react';
import { ButtonSize } from '../../model';
import { combineSx, PolymorphicComponentProps, PolymorphicRef, SxProps, toPixelString } from '../../utils';
import { BadgeProps } from '../Badge';
import { Box } from '../Box';
import { iconButtonPadding } from './icon-button-overrides';

type DefaultIconButtonRootComponent = 'button';

export type IconButtonVariant = 'secondary' | 'tertiary';

export interface BaseIconButtonProps {
  Badge?: React.ReactElement<BadgeProps>;
  children?: React.ReactNode;
  danger?: boolean;
  size?: ButtonSize;
  sx?: SxProps;
  variant?: IconButtonVariant;
}

export type IconButtonProps<RootComponent extends React.ElementType = DefaultIconButtonRootComponent> =
  PolymorphicComponentProps<RootComponent, BaseIconButtonProps>;

const BaseIconButton = <RootComponent extends React.ElementType = DefaultIconButtonRootComponent>(
  { Badge, disabled, size = 'medium', variant = 'tertiary', danger = false, children, sx, ...props }: IconButtonProps,
  ref?: PolymorphicRef<RootComponent>
) => {
  if (variant === 'secondary' && danger && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('Button variant "secondary" with danger prop is not supported');
  }

  return (
    <MuiIconButton
      {...props}
      ref={ref}
      color="default"
      disabled={disabled}
      size={size}
      sx={combineSx((theme) => {
        return danger ? dangerStylesByVariant.tertiary(theme) : stylesByVariant[variant](theme);
      }, sx)}
    >
      {children}
      {Badge && <Box sx={{ position: 'absolute', top: '-12px', right: '-4px' }}>{Badge}</Box>}
    </MuiIconButton>
  );
};

// Intentionally using some `any` here to around around some type errors before the casting. We have to do some casting
// here because forwardRef, generics, and TypeScript do not play nicely together.
// https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/forward_and_create_ref/#generic-forwardrefs
// TODO: clean this up when we are able to move to React 19, which no longer requires forwardRef.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const IconButton = forwardRef<any, BaseIconButtonProps>(BaseIconButton) as <
  RootComponent extends React.ElementType = DefaultIconButtonRootComponent,
>(
  props: IconButtonProps<RootComponent> & { ref?: PolymorphicRef<RootComponent> }
) => ReturnType<typeof BaseIconButton>;

const commonStyles: SystemStyleObject<Theme> = {
  [`&.${iconButtonClasses.sizeSmall}`]: {
    // Adjust the padding for each size to account for the 1px border
    padding: toPixelString(iconButtonPadding.small - 1),
    '.MuiSvgIcon-root': {
      width: '16px',
      height: '16px',
    },
  },
  [`&.${iconButtonClasses.sizeMedium}`]: {
    // Adjust the padding for each size to account for the 1px border
    padding: toPixelString(iconButtonPadding.medium - 1),
    '.MuiSvgIcon-root': {
      width: '20px',
      height: '20px',
    },
  },
};

const stylesByVariant: Record<IconButtonVariant, (theme: Theme) => SystemStyleObject<Theme>> = {
  // Tertiary are the default styles for MUI's IconButton and live
  // in icon-button-overrides.ts
  tertiary: (theme) => ({
    color: theme.featureFlags.panwRebrandStubYear
      ? theme.palette.button.standard.tertiary.text
      : theme.palette.text.secondary,

    [`&:hover`]: {
      color: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.button.standard.tertiary.text
        : theme.palette.text.primary,
    },

    [`&.${iconButtonClasses.sizeSmall}`]: {
      '.MuiSvgIcon-root': {
        width: '16px',
        height: '16px',
      },
    },
    [`&.${iconButtonClasses.sizeMedium}`]: {
      '.MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
      },
    },
  }),

  /**
   * Styles for the 'secondary' variant of IconButton.
   */
  secondary: (theme) => ({
    ...commonStyles,
    backgroundColor: theme.palette.button.standard.secondary.default,
    color: theme.palette.button.standard.secondary.text,
    border: `1px solid ${theme.palette.button.standard.secondary.borderColor.default}`,

    [`&:hover`]: {
      backgroundColor: theme.palette.button.standard.secondary.hover,
      border: `1px solid ${theme.palette.button.standard.secondary.borderColor.hover}`,
    },

    [`&.${iconButtonClasses.disabled}`]: {
      color: theme.palette.button.standard.secondary.textDisabled,
      border: `1px solid ${theme.palette.button.standard.secondary.borderColor.disabled}`,
    },
  }),
};

const dangerStylesByVariant: Record<'tertiary', (theme: Theme) => SystemStyleObject<Theme>> = {
  tertiary: (theme) => ({
    ...stylesByVariant.tertiary(theme),
    color: theme.palette.button.danger.tertiary.text,
    [`&:hover`]: {
      backgroundColor: theme.palette.button.danger.tertiary.hover,
    },

    [`&.${iconButtonClasses.disabled}`]: {
      color: theme.palette.button.danger.tertiary.textDisabled,
    },
  }),
};
